"use client";

import { ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { OTHER_GENRE } from "@/lib/utils";
import { GenreDrawer } from "./genre-drawer";

// Desktop wraps, so keep the row short; mobile scrolls horizontally, so more chips are cheap
const DESKTOP_COUNT = 3;
const MOBILE_COUNT = 8;

interface GenreFilterProps {
  genres: string[];
  genreCounts?: Map<string, number>;
  genreFilteredCount?: number;
  otherSubGenres: string[];
  selectedGenres: string[];
  onGenresChange: (genres: string[]) => void;
  genreMatchMode: "or" | "and";
  onMatchModeChange: (mode: "or" | "and") => void;
  onJukeboxPlay?: () => void;
  jukeboxReady?: boolean;
  children?: ReactNode;
}

export function GenreFilter({
  genres,
  genreCounts,
  genreFilteredCount,
  otherSubGenres,
  selectedGenres,
  onGenresChange,
  genreMatchMode,
  onMatchModeChange,
  onJukeboxPlay,
  jukeboxReady,
  children,
}: GenreFilterProps) {
  const t = useTranslations("Filters");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleGenre = (genre: string) => {
    if (selectedGenres.includes(genre)) {
      onGenresChange(selectedGenres.filter((g) => g !== genre));
    } else {
      onGenresChange([...selectedGenres, genre]);
    }
  };

  const mainGenres = useMemo(
    () => genres.filter((g) => g !== OTHER_GENRE),
    [genres]
  );

  const desktopGenres = mainGenres.slice(0, DESKTOP_COUNT);
  const mobileExtraGenres = mainGenres.slice(DESKTOP_COUNT, MOBILE_COUNT);
  const desktopOverflowCount =
    Math.max(0, mainGenres.length - DESKTOP_COUNT) + otherSubGenres.length;
  const mobileOverflowCount =
    Math.max(0, mainGenres.length - MOBILE_COUNT) + otherSubGenres.length;

  // Count selected genres hidden behind the "+N more" button, per breakpoint
  const desktopVisibleSet = new Set(desktopGenres);
  const mobileVisibleSet = new Set([...desktopGenres, ...mobileExtraGenres]);
  const selectedOverflowDesktop = selectedGenres.filter(
    (g) => !desktopVisibleSet.has(g)
  ).length;
  const selectedOverflowMobile = selectedGenres.filter(
    (g) => !mobileVisibleSet.has(g)
  ).length;

  const noneSelected = selectedGenres.length === 0;

  const genreChip = (genre: string, extraClass = "") => {
    const isSelected = selectedGenres.includes(genre);
    return (
      <button
        key={genre}
        onClick={() => toggleGenre(genre)}
        className={`cursor-pointer rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
          isSelected
            ? "border-acid bg-acid/10 text-acid"
            : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
        } ${extraClass}`}
      >
        {genre.toUpperCase()}
      </button>
    );
  };

  const moreButton = (count: number, highlighted: boolean, extraClass: string) => (
    <button
      onClick={() => setDrawerOpen(true)}
      className={`items-center gap-1 cursor-pointer rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
        highlighted
          ? "border-acid bg-acid/10 text-acid"
          : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
      } ${extraClass}`}
    >
      {t("moreGenres", { count })}
    </button>
  );

  return (
    <>
      {/* Single scroll row on mobile, wrapping row on desktop */}
      <div className="flex gap-2 max-sm:-mx-4 max-sm:overflow-x-auto max-sm:px-4 max-sm:no-scrollbar max-sm:scroll-fade-r sm:flex-wrap [&>*]:shrink-0 [&>*]:whitespace-nowrap">
        <button
          onClick={() => onGenresChange([])}
          className={`cursor-pointer rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
            noneSelected
              ? "border-acid bg-acid/10 text-acid"
              : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
          }`}
        >
          {t("allGenres")}
        </button>
        {desktopGenres.map((genre) => genreChip(genre))}
        {mobileExtraGenres.map((genre) => genreChip(genre, "sm:hidden"))}
        {mobileOverflowCount > 0 &&
          moreButton(mobileOverflowCount, selectedOverflowMobile > 0, "inline-flex sm:hidden")}
        {desktopOverflowCount > 0 &&
          moreButton(desktopOverflowCount, selectedOverflowDesktop > 0, "hidden sm:inline-flex")}
        {!noneSelected && (
          <button
            onClick={() => onGenresChange([])}
            className="cursor-pointer rounded-full border border-neutral-700 px-[calc(var(--spacing)*1.5)] py-1 font-mono text-xs text-neutral-400 transition-colors hover:border-red-500/50 hover:text-red-400"
            title={t("allGenres")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
              <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
        )}
        {children}
      </div>

      {drawerOpen && createPortal(
        <GenreDrawer
          genres={genres}
          genreCounts={genreCounts}
          genreFilteredCount={genreFilteredCount}
          otherSubGenres={otherSubGenres}
          selectedGenres={selectedGenres}
          onGenresChange={onGenresChange}
          genreMatchMode={genreMatchMode}
          onMatchModeChange={onMatchModeChange}
          onClose={() => setDrawerOpen(false)}
          onPlay={onJukeboxPlay ? () => { setDrawerOpen(false); onJukeboxPlay(); } : undefined}
          jukeboxReady={jukeboxReady}
        />,
        document.body
      )}
    </>
  );
}
